/**
 * Network resilience for lazily loaded 3D code and assets.
 *
 * `React.lazy` caches a rejected import for the life of the page. On a phone, one dropped
 * chunk would leave a scene blank until a full reload. `withRetry` retries with backoff
 * first, and while the browser reports it is offline it waits for the connection to return.
 *
 * @module
 */

/** Retry budget for {@link withRetry}. */
export interface RetryOptions {
  /** Retries after the first attempt. Default 3. */
  retries?: number
  /** Delay before the first retry; each later retry doubles it. Default 700 ms. */
  baseDelayMs?: number
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => globalThis.setTimeout(resolve, ms))
}

/** Resolves once the browser is online, immediately if it already is. */
export function whenOnline(): Promise<void> {
  if (typeof navigator === 'undefined' || navigator.onLine !== false) return Promise.resolve()
  return new Promise((resolve) => {
    globalThis.addEventListener('online', () => resolve(), { once: true })
  })
}

/**
 * Runs `task`, retrying failures with exponential backoff, and rethrows the last error once
 * the budget is spent.
 *
 * @example
 * const HeroScene = lazy(() => withRetry(() => import('./HeroScene')))
 */
export async function withRetry<T>(
  task: () => Promise<T>,
  { retries = 3, baseDelayMs = 700 }: RetryOptions = {},
): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await task()
    } catch (error) {
      if (attempt >= retries) throw error
      await whenOnline()
      await wait(baseDelayMs * 2 ** attempt)
    }
  }
}
